import { useMemo, useState } from "react";

// styles
import {
  ActivityLogListContainer,
  ActivityLogDisplay,
  NoDataMessage
} from "./ActivityLogList.styles.js";

// components
import SystemPaginationControls from "../system/pagination-controls/SystemPaginationControls.js";
import AccountUpdatedEntry from "../../pages/activity-log/components/account-updated-entry/AccountUpdatedEntry.js";
import CollectionUpdatedEntry from "../../pages/activity-log/components/collection-updated-entry/CollectionUpdatedEntry.js";
import TeamUpdatedEntry from "../../pages/activity-log/components/team-updated-entry/TeamUpdatedEntry.js";
import TokenPurchaseEntry from "../../pages/activity-log/components/token-purchase-entry/TokenPurchaseEntry.js";
import TokenRedemptionEntry from "../../pages/activity-log/components/token-redemption-entry/TokenRedemptionEntry.js";

export default function ActivityLogList({ 
  activities = [], 
  title = "Activity Log",
  itemsPerPage = 10,
  showPagination = true 
}) {
  const [currentPage, setCurrentPage] = useState(1);

  // Calculate pagination
  const totalPages = Math.ceil(activities.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedActivities = activities.slice(startIndex, endIndex);

  // Navigation functions
  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Render activity entries based on action type
  const renderedActivities = useMemo(() => {
    return paginatedActivities.map((activity) => {
      switch (activity.actionType) {
        case "updatedTeam":
          return <TeamUpdatedEntry key={activity.timestamp} content={activity} />;
        case "updatedAccount":
          return <AccountUpdatedEntry key={activity.timestamp} content={activity} />;
        case "addedToCollection":
        case "removedFromCollection":
        case "soldFromCollection":
        case "returnedToCollection":
          return <CollectionUpdatedEntry key={activity.timestamp} content={activity} />;
        case "tokenPurchase":
          return <TokenPurchaseEntry key={activity.timestamp} content={activity} />;
        case "checklistUnlocked":
        case "promoCodeRedeemed":
        case "welcomeTokensGranted":
          return <TokenRedemptionEntry key={activity.timestamp} content={activity} />;
        default:
          return (
            <div key={activity.id || activity.timestamp} style={{ 
              color: "#FFFFFF", 
              padding: "16px", 
              background: "rgba(255,255,255,0.1)", 
              borderRadius: "8px",
              marginBottom: "12px"
            }}>
              <strong>Unknown Activity Type:</strong> {activity.actionType}
              <pre style={{ fontSize: "12px", marginTop: "8px" }}>
                {JSON.stringify(activity, null, 2)}
              </pre>
            </div>
          );
      }
    });
  }, [paginatedActivities]);

  return (
    <ActivityLogListContainer>
      {title && <h3>{title}</h3>}
      
      <ActivityLogDisplay>
        {activities.length === 0 ? (
          <NoDataMessage>No activities found.</NoDataMessage>
        ) : paginatedActivities.length === 0 ? (
          <NoDataMessage>No activities found for this page.</NoDataMessage>
        ) : (
          <>
            {renderedActivities}

            {showPagination && (
              <SystemPaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
                showLoadingIndicator={false}
              />
            )}
          </>
        )}
      </ActivityLogDisplay>
    </ActivityLogListContainer>
  );
}
